// enhancedLocationTracker.js - Auto-log every 5 minutes
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// Active tracking interval
let trackingInterval = null;

/**
 * Start background GPS tracking
 * Logs location every 5 minutes automatically
 */
export const startLocationTracking = (user) => {
  // Stop any existing tracking
  stopLocationTracking();

  // Initial capture
  captureAndLogLocation(user);

  // Start 5-minute interval
  trackingInterval = setInterval(() => {
    captureAndLogLocation(user);
  }, 5 * 60 * 1000); // 5 minutes

  console.log('📍 GPS tracking started - logging every 5 minutes');
};

/**
 * Stop background GPS tracking
 */
export const stopLocationTracking = () => {
  if (trackingInterval) {
    clearInterval(trackingInterval);
    trackingInterval = null;
    console.log('📍 GPS tracking stopped');
  }
};

/**
 * Capture current location and log to Firebase
 */
const captureAndLogLocation = async (user) => {
  if (!navigator.geolocation) {
    console.error('GPS not supported');
    return null;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date()
        };

        // Log to Firebase
        try {
          await addDoc(collection(db, 'locationLogs'), {
            userId: user.id,
            userName: user.name,
            userRole: user.role,
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy,
            timestamp: serverTimestamp(),
            capturedAt: new Date().toISOString(),
            action: 'auto_tracking',
            page: 'background'
          });

          console.log(`📍 Location logged: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`);
        } catch (error) {
          console.error('Error logging location:', error);
        }

        resolve(location);
      },
      (error) => {
        console.error('GPS error:', error);
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000 // Cache for 1 minute
      }
    );
  });
};

/**
 * Get current location once
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GPS not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date()
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

/**
 * Format coordinates for display
 */
export const formatCoordinates = (lat, lng) => {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

/**
 * Open location in Google Maps
 */
export const openInGoogleMaps = (lat, lng) => {
  const url = `https://www.google.com/maps?q=${lat},${lng}`;
  window.open(url, '_blank');
};

/**
 * Calculate distance between two points (in meters)
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distance in meters
};
